import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import type { ApiClientError } from "./_errors";
import { apiJson } from "./_http";
import type {
	LineupEntry,
	LineupPick,
	PowerRankingSummary,
	StandingsResponse,
	WeeklyLeaguesResponse,
} from "../types";

// TanStack Query hooks for the fantasy service. Same wiring pattern as
// lib/api/ideas.ts — all HTTP calls flow through the web-app's Pages Functions
// at /api/leagues/* and /api/me/power-ranking so the session cookie
// verification stays server-side.

// --- Theme rosters (static mirror of services-cf/fantasy themes) ------------
//
// The fantasy svc doesn't expose a "list this theme's roster" endpoint
// (pre-seed scope per Phase 1.md §3.7). We mirror the same static lists here
// so the LineupBuilder can render symbol pickers + validate locally before
// submission. ValidateLineup on the server is still the source of truth — if
// these drift, the server rejects with `lineup_out_of_roster` (and the UI
// surfaces that error inline).

export const THEME_ROSTERS: Record<string, readonly string[]> = {
	mag_7: ["META", "AAPL", "AMZN", "MSFT", "GOOGL", "NVDA", "TSLA"],
	div_aristocrats: [
		"JNJ",
		"PG",
		"KO",
		"PEP",
		"MMM",
		"CAT",
		"MCD",
		"WMT",
		"CVX",
		"XOM",
		"CL",
		"ED",
		"EMR",
		"GPC",
		"HRL",
		"ITW",
		"KMB",
		"LOW",
		"NUE",
		"PPG",
		"SHW",
		"SPGI",
		"SYY",
		"T",
		"TGT",
	],
	under_10: [
		"F",
		"PLTR",
		"SOFI",
		"NIO",
		"SNAP",
		"RIG",
		"BBD",
		"ITUB",
		"VALE",
		"GOLD",
		"BAC",
		"T",
		"PFE",
		"VZ",
		"INTC",
		"WBA",
		"CSCO",
		"NOK",
		"AAL",
		"DAL",
		"UAL",
		"CCL",
		"NCLH",
		"RIVN",
		"LCID",
		"SIRI",
		"HBAN",
		"KEY",
		"FCEL",
		"PLUG",
		"CHPT",
		"AMC",
		"BB",
		"GME",
		"GPS",
		"M",
		"KSS",
		"JWN",
		"WBD",
		"PARA",
		"NWSA",
		"TLRY",
		"ACB",
		"CRON",
		"SNDL",
		"MARA",
		"RIOT",
		"BITF",
		"WULF",
		"HUT",
	],
	quality: [
		"MSFT",
		"GOOGL",
		"META",
		"AAPL",
		"NVDA",
		"V",
		"MA",
		"ADBE",
		"ORCL",
		"AVGO",
		"COST",
		"NKE",
		"LULU",
		"TJX",
		"ROST",
		"MNST",
		"KO",
		"PEP",
		"PG",
		"CL",
		"ACN",
		"INTU",
		"ISRG",
		"CMG",
		"FAST",
		"PAYX",
	],
	anti_mag_7: [
		"JPM",
		"V",
		"MA",
		"WMT",
		"JNJ",
		"UNH",
		"PG",
		"HD",
		"DIS",
		"BAC",
		"XOM",
		"CVX",
		"COST",
		"ABBV",
		"MRK",
		"PFE",
		"KO",
		"PEP",
		"CSCO",
		"ORCL",
		"CRM",
		"ADBE",
		"NFLX",
		"INTC",
		"AMD",
		"AVGO",
		"TXN",
		"QCOM",
		"NKE",
		"MCD",
		"SBUX",
		"TGT",
		"LOW",
		"T",
		"VZ",
		"TMUS",
		"CMCSA",
		"BA",
		"CAT",
		"DE",
		"GS",
		"MS",
		"WFC",
		"C",
		"AXP",
		"BLK",
		"SPGI",
		"ICE",
		"CME",
		"BRK.B",
	],
};

// --- Query keys --------------------------------------------------------------

export const leagueKeys = {
	all: ["leagues"] as const,
	weekly: () => [...leagueKeys.all, "weekly"] as const,
	standings: (id: string) => [...leagueKeys.all, "standings", id] as const,
	powerRanking: () => ["me", "power-ranking"] as const,
};

// --- Hooks -------------------------------------------------------------------

// useWeeklyLeagues — current week's 5 themed public leagues. Refreshes every
// minute so newly-opened leagues show up without a manual reload.
export function useWeeklyLeagues(): UseQueryResult<WeeklyLeaguesResponse> {
	return useQuery({
		queryKey: leagueKeys.weekly(),
		queryFn: () => apiJson<WeeklyLeaguesResponse>("/api/leagues/weekly"),
		staleTime: 60_000,
	});
}

// useLeague — convenience wrapper that pulls a single weekly league out of the
// weekly response. The fantasy service doesn't expose a per-id GET so we
// re-derive from the list.
export function useLeague(id: string | undefined): {
	data: WeeklyLeaguesResponse["leagues"][number] | undefined;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
} {
	const weekly = useWeeklyLeagues();
	const league = id
		? weekly.data?.leagues.find((l) => l.id === id || l.theme === id)
		: undefined;
	return {
		data: league,
		isLoading: weekly.isLoading,
		isError: weekly.isError,
		error: weekly.error,
	};
}

// useLeagueStandings — live leaderboard for a league. Polls every 30s so the
// page reflects ranking shuffles after the cron settle runs.
export function useLeagueStandings(
	id: string | undefined,
): UseQueryResult<StandingsResponse> {
	return useQuery({
		queryKey: leagueKeys.standings(id ?? "missing"),
		queryFn: () => {
			if (!id) throw new Error("missing league id");
			return apiJson<StandingsResponse>(
				`/api/leagues/${encodeURIComponent(id)}/standings`,
			);
		},
		enabled: !!id && id.length > 0,
		refetchInterval: 30_000,
	});
}

// useSubmitLineup — POSTs a (symbol, allocation%) × 10 lineup. Invalidates the
// weekly list (entry count) + the standings on success.
export function useSubmitLineup(
	leagueId: string,
): UseMutationResult<LineupEntry, ApiClientError, LineupPick[]> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (picks: LineupPick[]) =>
			apiJson<LineupEntry>(`/api/leagues/${encodeURIComponent(leagueId)}/entries`, {
				method: "POST",
				body: JSON.stringify({ picks }),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: leagueKeys.weekly() });
			void qc.invalidateQueries({
				queryKey: leagueKeys.standings(leagueId),
			});
		},
	});
}

// useMyPowerRanking — the caller's Elo + W/L/D record. Phase 5 settles
// leagues weekly; this query just reads the current snapshot.
export function useMyPowerRanking(): UseQueryResult<PowerRankingSummary> {
	return useQuery({
		queryKey: leagueKeys.powerRanking(),
		queryFn: () => apiJson<PowerRankingSummary>("/api/me/power-ranking"),
		staleTime: 60_000,
	});
}
